import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AuthService, ForgotPasswordCommand } from '@anihis/core/data-access';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { TranslocoService, translate } from '@jsverse/transloco';
import { getEmailRequestLanguage } from '@anihis/shared/src/functions/email-request-language';
import { catchError, finalize, of, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { LoadingStatus } from '../../shared/enums/loading';
import { extractServerErrorMessage } from '../../shared/functions/extract-server-error';

type ForgotPasswordState = {
  status: LoadingStatus;
  error: string | null;
  submitted: boolean;
  submittedEmail: string | null;
};

/** Portal forgot-password account types (managers and sub-managers share RoleType.Manager). */
function normalizePortalForgotPasswordRole(role: string): string {
  const value = (role || 'ClinicOwner').trim().toLowerCase();
  if (value === 'clinic') return 'Clinic';
  if (value === 'manager') return 'Manager';
  return 'ClinicOwner';
}

const initialState: ForgotPasswordState = {
  status: LoadingStatus.Loaded,
  error: null,
  submitted: false,
  submittedEmail: null,
};

export const ForgotPasswordStore = signalStore(
  withState(initialState),
  withMethods(
    (
      store,
      authService = inject(AuthService),
      router = inject(Router),
      snackBar = inject(MatSnackBar),
      transloco = inject(TranslocoService),
    ) => ({
      sendResetLink(email: string, role: string) {
        patchState(store, { status: LoadingStatus.Loading, error: null });

        const normalizedRole = normalizePortalForgotPasswordRole(role);

        const command: ForgotPasswordCommand = {
          email,
          role: normalizedRole as ForgotPasswordCommand['role'],
          language: getEmailRequestLanguage(transloco.getActiveLang()),
        };

        return authService.authForgotPasswordPost(command).pipe(
          tap(() => {
            patchState(store, {
              submitted: true,
              submittedEmail: email,
            });
          }),
          finalize(() => patchState(store, { status: LoadingStatus.Loaded })),
          catchError((err) => {
            const errorMsg = extractServerErrorMessage(
              err,
              'Failed to send reset password email. Please try again.',
            );

            patchState(store, { error: errorMsg });

            snackBar.open(errorMsg, translate('Close'), {
              duration: 7000,
              horizontalPosition: 'center',
              verticalPosition: 'top',
              panelClass: ['error-snackbar'],
            });

            return of(null);
          }),
        );
      },

      resetError() {
        patchState(store, { error: null });
      },

      reset() {
        patchState(store, initialState);
      },
    }),
  ),
);
